import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCalendarDays,
  faClock,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../components/Button";
import ShareButtons from "../components/ShareButtons";
import { scrollToElement } from "../utils/helpers";
import { apiUrl, getApiBaseUrl, resolveMediaUrl } from "../utils/api";
import { SITE_URL, buildCanonicalUrl, localizePath } from "../utils/language";
import { applySeo, setPageJsonLd } from "../utils/seo";
import "./BlogPostPage.css";

const BlogPostPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setError("missing_slug");
      return undefined;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          apiUrl(`/api/v1/blog/posts/${encodeURIComponent(slug)}/`),
          { signal: controller.signal }
        );
        if (response.status === 404) {
          setPost(null);
          setError("not_found");
          return;
        }
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        setPost(data);
      } catch (e) {
        if (e.name === "AbortError") return;
        setError("fetch");
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [slug]);

  const getLocalizedValue = (item, key) => {
    if (!item) return "";
    if (i18n.language === "uz" && item[`${key}_uz`]) {
      return item[`${key}_uz`];
    }
    if (i18n.language === "en" && item[`${key}_en`]) {
      return item[`${key}_en`];
    }
    return item[key] || item[`${key}_en`] || item[`${key}_uz`] || "";
  };

  // Мета-теги и structured data статьи: у каждой свой title, description,
  // canonical и разметка Article — иначе все статьи выглядят для поиска
  // как дубли главной страницы.
  useEffect(() => {
    const path = `/blog/posts/${slug}`;

    if (!post) {
      applySeo({
        language: i18n.language,
        path,
        title: `${t("blog_post.not_found")} ${t("seo.blog.title_suffix")}`,
        description: t("seo.blog.description_fallback"),
        noindex: true,
      });
      setPageJsonLd(null);
      return undefined;
    }

    const postTitle = getLocalizedValue(post, "title");
    const postExcerpt = getLocalizedValue(post, "excerpt");
    const image =
      resolveMediaUrl(post.featured_image) || `${SITE_URL}/hero-bg1.webp`;
    const canonical = buildCanonicalUrl(i18n.language, path);

    applySeo({
      language: i18n.language,
      path,
      title: `${postTitle} ${t("seo.blog.title_suffix")}`.trim(),
      description: postExcerpt || t("seo.blog.description_fallback"),
      ogType: "article",
      ogImage: image,
    });

    setPageJsonLd({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `${canonical}#article`,
          headline: postTitle,
          description: postExcerpt || undefined,
          image,
          inLanguage: i18n.language,
          datePublished: post.published_at || post.created_at || undefined,
          dateModified: post.updated_at || post.published_at || undefined,
          mainEntityOfPage: canonical,
          author: { "@id": `${SITE_URL}/#organization` },
          publisher: { "@id": `${SITE_URL}/#organization` },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: t("blog_post.breadcrumb_home"),
              item: buildCanonicalUrl(i18n.language, "/"),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: t("blog_post.breadcrumb_products"),
              item: `${buildCanonicalUrl(i18n.language, "/")}#products`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: postTitle,
              item: canonical,
            },
          ],
        },
      ],
    });

    return () => setPageJsonLd(null);
  }, [post, slug, i18n.language, t]);

  /** ISO string → UTC bo‘yicha dd.mm.yyyy */
  const formatUtc = (iso) => {
    if (iso == null || iso === "") return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const pad = (n) => String(n).padStart(2, "0");
    const day = pad(d.getUTCDate());
    const month = pad(d.getUTCMonth() + 1);
    const year = d.getUTCFullYear();
    return `${day}.${month}.${year}`;
  };

  const homePath = localizePath(i18n.language, "/");

  const handleBackProducts = () => {
    navigate({ pathname: homePath, hash: "#products" });
  };

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="container blog-post-loading">
          <p>{t("blog_post.loading")}</p>
        </div>
      </div>
    );
  }

  if (error === "missing_slug") {
    return (
      <div className="blog-post-page">
        <div className="container blog-post-error">
          <p>{t("blog_post.not_found")}</p>
          <Button variant="primary" onClick={() => navigate(homePath)}>
            {t("blog_post.back_home")}
          </Button>
        </div>
      </div>
    );
  }

  if (error === "not_found" || !post) {
    return (
      <div className="blog-post-page">
        <div className="container blog-post-error">
          <h1 className="blog-post-not-found-title">
            {t("blog_post.not_found")}
          </h1>
          <Button variant="primary" onClick={handleBackProducts}>
            <FontAwesomeIcon
              icon={faArrowLeft}
              className="blog-post-btn-icon"
            />
            {t("blog_post.back_products")}
          </Button>
        </div>
      </div>
    );
  }

  if (error === "fetch") {
    return (
      <div className="blog-post-page">
        <div className="container blog-post-error">
          <p>{t("blog_post.error")}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            {t("blog_post.retry")}
          </Button>
        </div>
      </div>
    );
  }

  const title = getLocalizedValue(post, "title");
  const excerpt = getLocalizedValue(post, "excerpt");
  /** Backenddan kelgan HTML — tanlangan til bo’yicha bitta qilib chiqariladi */
  const rawContent = getLocalizedValue(post, "content");
  const apiBase = getApiBaseUrl();
  const htmlContent = apiBase
    ? rawContent.replace(
        /(<img\s[^>]*src=["’])(\/media\/)/gi,
        `$1${apiBase}/media/`
      )
    : rawContent;
  const viewsCount =
    typeof post.views_count === "number" ? post.views_count : null;
  const createdUtc = formatUtc(post.created_at);
  const publishedUtc = formatUtc(post.published_at);

  return (
    <div className="blog-post-page">
      <div className="container blog-post-main">
        <div className="blog-post-meta-bar flex items-center">
          <div className="blog-post-meta-bar-item flex items-center">
            <FontAwesomeIcon icon={faEye} className="blog-post-meta-bar-icon" />
            <span>
              {viewsCount != null
                ? `${viewsCount} ${t("blog_post.views")}`
                : "—"}
            </span>
          </div>
          {publishedUtc ? (
            <div className="blog-post-meta-bar-item flex items-center">
              <FontAwesomeIcon
                icon={faCalendarDays}
                className="blog-post-meta-bar-icon"
              />
              <span title={t("blog_post.label_published_at")}>
                {publishedUtc}
              </span>
            </div>
          ) : null}
          {createdUtc ? (
            <div className="blog-post-meta-bar-item flex items-center">
              <FontAwesomeIcon
                icon={faClock}
                className="blog-post-meta-bar-icon"
              />
              <span title={t("blog_post.label_created_at")}>{createdUtc}</span>
            </div>
          ) : null}
        </div>
        <h1 className="blog-post-heading-title">{title}</h1>
        {excerpt ? <p className="blog-post-heading-excerpt">{excerpt}</p> : null}
        <motion.article
          className="blog-post-article blog-post-article--body"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
        >
          {htmlContent ? (
            <div
              className="blog-post-content"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          ) : (
            <p className="blog-post-empty">{t("blog_post.no_content")}</p>
          )}
        </motion.article>

        <div className="blog-post-share">
          <ShareButtons
            path={localizePath(i18n.language, `/blog/posts/${slug}`)}
            title={title}
          />
        </div>

        {/* <div className="blog-post-footer-cta">
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              navigate({ pathname: homePath, hash: "#contact-form" });
              setTimeout(() => scrollToElement("contact-form"), 200);
            }}
          >
            {t("blog_post.cta_order")}
          </Button>
        </div> */}
      </div>
    </div>
  );
};

export default BlogPostPage;
